import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, ChevronDown, Download, Menu, X, Target, Lightbulb,
  TrendingUp, Handshake, Cloud, Server, Brain, Code2, Shield, Database,
  Clock, Award, Users, Star, Quote, Sparkles, Zap, Building2, Play,
  ArrowUpRight, CircleDot, Layers, GitBranch, Rocket, Briefcase,
  UserCheck, ThumbsUp, Calendar, FileCheck, BarChart, ClipboardList,
  FileText, GitPullRequest, CheckCircle, Timer, DollarSign, Package,
  Settings, RefreshCw, Layout, Monitor, ShieldCheck,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ============ COLOR PALETTE ============
const COLORS = {
  primary: '#001534',
  secondary: '#FFFFFF',
  accent: '#6366F1',
  accentLight: '#818CF8',
  accentDark: '#4F46E5',
  purple: '#A855F7',
  cyan: '#06B6D4',
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  gradientStart: '#6366F1',
  gradientEnd: '#A855F7',
};

// ============ ANIMATED COUNTER ============
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ============ HERO ============
const Hero = () => {
  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.accent }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.purple }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${COLORS.white} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.white} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-wide mb-6">
              <span style={{ color: COLORS.white }}>Statement of Work (SOW)</span>
              <br />
              <span style={{ color: COLORS.white }}>Services for </span>
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentLight}, ${COLORS.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                IT Projects
              </span>
            </h1>

            <p className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)' }}>
              Fixed-scope project delivery with guaranteed results. Outsource complex IT projects to expert teams with defined deliverables and timelines.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                className="group flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base transition-all hover:shadow-2xl hover:shadow-indigo-500/40"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                  color: COLORS.white,
                }}
              >
                Submit Your Project
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border transition-all"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: COLORS.white,
                }}
              >
                How It Works
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 98, suffix: '%', label: 'On-Time Delivery' },
                { value: 500, suffix: '+', label: 'SOW Projects Delivered' },
                { value: 5, suffix: 'M+', label: 'Project Value Delivered', prefix: '$' },
                { value: 4.8, suffix: '/5', label: 'Client Satisfaction Rating' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="border-l-2 pl-4"
                  style={{ borderColor: COLORS.accent }}
                >
                  <div className="text-3xl font-semibold" style={{ color: COLORS.white }}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden h-[400px] shadow-2xl">
              <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Staff%20Augementation/sow.jpg"
                alt="Project planning and delivery"
                className="w-full h-full object-fit"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.2))`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WHY CHOOSE SOW ============
const WhySOW = () => {
  const features = [
    {
      icon: ClipboardList,
      title: 'Fixed Scope & Budget',
      desc: 'Clear deliverables and defined budget from day one. No scope creep, no surprise costs. Complete transparency and accountability.',
      color: COLORS.accent,
    },
    {
      icon: Timer,
      title: 'Guaranteed Timeline',
      desc: 'Defined project milestones and delivery dates. 98% on-time delivery track record. Penalties for missed deadlines built into contract.',
      color: COLORS.purple,
    },
    {
      icon: DollarSign,
      title: 'Deliverable-Based Payment',
      desc: 'Pay upon completion of specific milestones. Not time-based—result-based. Ensures accountability and alignment on results.',
      color: COLORS.cyan,
    },
    {
      icon: ShieldCheck,
      title: 'Quality Guarantee',
      desc: 'Defined quality standards and acceptance criteria. Full testing, documentation, and knowledge transfer included.',
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="why-sow"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-7xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Why Choose Statement of Work Engagement?
          </h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: COLORS.gray600 }}>
            SOW-based projects provide predictability, fixed costs, and guaranteed deliverables for your IT initiatives.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: feat.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `linear-gradient(135deg, ${feat.color}30, ${feat.color}10)`,
                      border: `1px solid ${feat.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: feat.color }} />
                  </div>

                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                    {feat.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ PROJECT TYPES ============
const ProjectTypes = () => {
  const [active, setActive] = useState(0);

  const projects = [
    {
      icon: Rocket,
      title: 'Custom Development',
      desc: 'Web applications, mobile apps, backend systems. Fully custom builds with fixed scope and timeline.',
      color: COLORS.accent,
    },
    {
      icon: Cloud,
      title: 'Cloud Migration',
      desc: 'Move from on-premise to AWS/Azure/GCP. Assessment, architecture, implementation, testing—all in one SOW.',
      color: COLORS.cyan,
    },
    {
      icon: RefreshCw,
      title: 'Legacy Modernization',
      desc: 'Refactor legacy systems to modern architecture. Phased approach with clear deliverables per phase.',
      color: COLORS.purple,
    },
    {
      icon: Database,
      title: 'Data Engineering',
      desc: 'Data pipeline development, ETL implementation, analytics platform setup. End-to-end delivery.',
      color: '#F59E0B',
    },
    {
      icon: GitPullRequest,
      title: 'System Integration',
      desc: 'API development, third-party integrations, system connectivity. Full integration with testing.',
      color: '#10B981',
    },
    {
      icon: Settings,
      title: 'Infrastructure Setup',
      desc: 'Kubernetes deployment, CI/CD pipelines, infrastructure automation. Production-ready setup.',
      color: '#EC4899',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
      id="project-types"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-5xl mx-auto mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.white }}>
            Types of Projects We Handle
          </h2>
          <p className="text-lg text-center" style={{ color: 'rgba(255,255,255,0.7)' }}>
            From custom development to enterprise migrations, we deliver SOW-based IT projects across all domains.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {projects.map((project, i) => {
            const Icon = project.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="group relative p-8 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.03)',
                  borderColor: 'rgba(255,255,255,0.1)',
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: project.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `${project.color}20`,
                      border: `1px solid ${project.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: project.color }} />
                  </div>

                  <h3 className="text-xl font-bold mb-3" style={{ color: COLORS.white }}>
                    {project.title}
                  </h3>
                  <p className="leading-relaxed text-sm" style={{ color: 'rgba(255,255,255,0.7)' }}>
                    {project.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ PROCESS ============
const Process = () => {
  const steps = [
    {
      number: '1',
      title: 'Project Discovery',
      desc: 'Detailed requirements gathering, scope definition, timeline estimation.',
      icon: Target,
      color: COLORS.accent,
    },
    {
      number: '2',
      title: 'SOW Creation',
      desc: 'Formal statement of work with deliverables, milestones, costs, and timeline.',
      icon: FileText,
      color: COLORS.purple,
    },
    {
      number: '3',
      title: 'Team Assignment',
      desc: 'Dedicated project team with architect, developers, QA, and project manager.',
      icon: Users,
      color: COLORS.cyan,
    },
    {
      number: '4',
      title: 'Development & Delivery',
      desc: 'Agile execution with milestone reviews, testing, documentation. Regular status updates.',
      icon: Code2,
      color: '#F59E0B',
    },
    {
      number: '5',
      title: 'Deployment & Support',
      desc: 'Production deployment, knowledge transfer, post-launch support included.',
      icon: Rocket,
      color: '#10B981',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="process"
    >
      <div className="max-w-8xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Statement of Work Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            5-step process from project definition to delivery and closure.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-5 gap-6">
          {steps.map((step, i) => {
            const Icon = step.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="relative p-6 rounded-3xl border text-center transition-all"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                {/* Connector line */}
                {i < steps.length - 1 && (
                  <div
                    className="hidden md:block absolute top-1/2 -right-3 w-6 h-px"
                    style={{ backgroundColor: COLORS.gray300 }}
                  />
                )}

                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background: `linear-gradient(135deg, ${step.color}30, ${step.color}10)`,
                    border: `1px solid ${step.color}40`,
                  }}
                >
                  <Icon className="w-6 h-6" style={{ color: step.color }} />
                </div>

                <div
                  className="text-md font-bold mb-2"
                  style={{ color: step.color }}
                >
                  Step {step.number}
                </div>

                <h3 className="text-md font-bold mb-2" style={{ color: COLORS.primary }}>
                  {step.title}
                </h3>
                <p className="text-md leading-relaxed" style={{ color: COLORS.gray600, wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none', whiteSpace: 'normal' }}>
                  {step.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ FAQ ============
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: "What's included in a typical SOW?",
      a: 'A comprehensive SOW includes: detailed project scope, deliverables list, timeline with milestones, fixed budget/pricing, team composition, quality standards, acceptance criteria, testing scope, documentation requirements, knowledge transfer plan, and post-launch support terms. Everything is clearly defined before work begins.',
    },
    {
      q: 'How long does it take to create an SOW?',
      a: 'SOW creation typically takes 2-4 weeks depending on project complexity. We conduct detailed discovery sessions, analyze requirements, assess risks, and prepare comprehensive documentation. Complex enterprise projects may take 4-6 weeks. Once agreed, execution can start immediately.',
    },
    {
      q: 'What if requirements change during the project?',
      a: 'Changes are handled through a formal change request process. Any scope modifications require written agreement on impact to timeline, budget, and deliverables. This protects both parties and maintains project control. We\'re flexible but ensure changes don\'t derail the project.',
    },
    {
      q: 'What happens if we miss a milestone?',
      a: 'SOW includes defined penalties for missed milestones (typically 1-2% of project value per week). We take milestones seriously and have 98% on-time delivery rate. Penalties are waived if delays are due to client-side factors (approval delays, requirement changes not included in SOW).',
    },
    {
      q: 'How is quality ensured in SOW projects?',
      a: 'Quality is built into SOW with defined acceptance criteria, testing requirements, code review standards, and performance metrics. We include comprehensive QA testing, user acceptance testing (UAT), security testing where applicable. Deliverables must meet quality standards before payment is released.',
    },
    {
      q: 'What post-launch support is included?',
      a: 'Most SOWs include 30-90 days post-launch support for bug fixes, performance optimization, and minor adjustments. We monitor production, respond to critical issues, and provide knowledge transfer documentation. Extended support beyond initial period is available at agreed rates.',
    },
  ];

  return (
    <section className="relative py-16 overflow-hidden" style={{ backgroundColor: COLORS.primary }} id="faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.secondary }}>
            Statement of Work FAQs
          </h2>
        </motion.div>

        <div className="space-y-3 bg-white p-5 rounded-3xl border">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden transition-all"
              style={{
                backgroundColor: '#ffffff',
                borderColor: open === i ? 'rgba(99, 102, 241, 0.3)' : COLORS.gray200,
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: COLORS.primary }}>
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: open === i ? COLORS.accent : COLORS.gray200 }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: open === i ? COLORS.white : COLORS.primary }} />
                </motion.div>
              </button>

              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 leading-relaxed text-justify" style={{ color: COLORS.gray600, wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none', whiteSpace: 'normal' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTA = () => {
  const COLORS = {
    bg: '#F8FAFC',
    heading: '#0F172A',
    muted: '#475569',
    accent: '#6366F1',
    accentLight: '#A5B4FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6"
            style={{ color: COLORS.heading }}
          >
            Deliver Your Project with Confidence
          </h2>

          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-10"
            style={{ color: COLORS.muted }}
          >
            Fixed-scope, deliverable-based engagement with 98% on-time delivery track record. Let's discuss your project requirements.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base"
              style={{
                backgroundColor: COLORS.accent,
                color: COLORS.white,
              }}
            >
              Get Your SOW Quote
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ MAIN APP ============
export default function StatementOfWork() {
  return (
    <div style={{ backgroundColor: COLORS.secondary, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Hero />
      <WhySOW />
      <ProjectTypes />
      <Process />
      <FAQ />
      <FinalCTA />
    </div>
  );
}
